import { CheckCircle2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import type { PersonListItem } from '@/types'
import type { OrgBranch } from '@/types/organization'

export interface CreateVirtualUserFormData {
	name: string
	crId: string
	branchId: string
}

interface CreateVirtualUserFormProps {
	branches: OrgBranch[]
	isLoading?: boolean
	successData?: PersonListItem | null
	onSave: (data: CreateVirtualUserFormData) => void
	onClose: () => void
}

export function CreateVirtualUserFormContent({
	branches,
	isLoading,
	successData,
	onSave,
	onClose,
}: CreateVirtualUserFormProps) {
	const { t } = useTranslation()
	const [name, setName] = useState('')
	const [branchId, setBranchId] = useState('')
	const [crId, setCrId] = useState('')

	const selectedBranch = branches.find((b) => b.id === branchId)
	const crOptions = selectedBranch?.crs ?? []

	const handleBranchChange = (value: string) => {
		setBranchId(value)
		setCrId('')
	}

	const handleSave = () => {
		if (!name.trim()) {
			toast.error(t('administration.users.form.emptyNameError'))
			return
		}
		if (!branchId) {
			toast.error(t('administration.users.form.emptyBranchError'))
			return
		}
		if (!crId) {
			toast.error(t('administration.users.form.emptyCrError'))
			return
		}
		onSave({ name: name.trim(), crId, branchId })
	}

	if (successData) {
		return (
			<div className="flex flex-col items-center gap-5 px-1 py-4 text-center">
				<CheckCircle2 className="size-14 text-green-500" />
				<div className="flex flex-col gap-1">
					<p className="text-base font-semibold">{successData.name}</p>
					<p className="text-sm text-muted-foreground">
						{t('administration.users.form.badgeLabel')}:&nbsp;
						<span className="font-mono font-bold text-foreground">
							{successData.badge}
						</span>
					</p>
					<p className="text-xs text-muted-foreground">{successData.crName}</p>
				</div>
				<Button
					type="button"
					onClick={onClose}
					className="mt-2 w-full bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{t('administration.users.form.closeButton')}
				</Button>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="virtual-user-name">
					{t('administration.users.form.nameLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="virtual-user-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t('administration.users.form.namePlaceholder')}
						maxLength={100}
					/>
				</FieldContent>
			</Field>

			<Field>
				<FieldLabel htmlFor="virtual-user-branch">
					{t('administration.users.form.branchLabel')}
				</FieldLabel>
				<FieldContent>
					<NativeSelect
						id="virtual-user-branch"
						value={branchId}
						onChange={(e) => handleBranchChange(e.target.value)}
					>
						<NativeSelectOption value="">
							{t('administration.users.form.branchPlaceholder')}
						</NativeSelectOption>
						{branches.map((branch) => (
							<NativeSelectOption key={branch.id} value={branch.id}>
								{branch.name}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</FieldContent>
			</Field>

			<Field>
				<FieldLabel htmlFor="virtual-user-cr">
					{t('administration.users.form.crLabel')}
				</FieldLabel>
				<FieldContent>
					<NativeSelect
						id="virtual-user-cr"
						value={crId}
						onChange={(e) => setCrId(e.target.value)}
						disabled={!branchId}
					>
						<NativeSelectOption value="">
							{t('administration.users.form.crPlaceholder')}
						</NativeSelectOption>
						{crOptions.map((cr) => (
							<NativeSelectOption key={cr.id} value={cr.id}>
								{cr.name}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</FieldContent>
			</Field>

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					disabled={isLoading}
					className="flex-1"
				>
					{t('administration.users.form.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={isLoading}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{isLoading ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						t('administration.users.form.createButton')
					)}
				</Button>
			</div>
		</div>
	)
}
